import { Directive, Host, HostListener, Input, OnChanges, OnInit, Renderer2, SimpleChanges } from '@angular/core';
import { Key } from 'ts-key-enum';
import { Table } from 'primeng/table';
import { isTextSelectionRowClickIntent, type PointerDownCoordinates } from '@zaoblako/web/shared/helpers/has-non-empty-text-selection.helper';

@Directive({
  selector: '[crmVisualSelectedRowsTable]',
  standalone: true
})
export class VisualSelectedRowsTableDirective implements OnInit, OnChanges {
  @Input() public isVisualSelected = true;
  @Input() public keySelectedRows: string;
  @Input() public loading: boolean;

  public visualSelectedRows: string[] = [];
  private indexVisualSelectedRows = 0;

  private rowsBody: HTMLTableRowElement[] = [];
  private timer: ReturnType<typeof setTimeout>;

  public constructor(private readonly renderer: Renderer2, @Host() private readonly table: Table) {}

  public ngOnInit() {
    if (this.isVisualSelected) {
      this.setTimer();
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.loading?.currentValue === false && this.isVisualSelected) {
      this.setTimer();
    }
  }

  @HostListener('window:keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent) {
    if (this.visualSelectedRows?.length === 1) {
      this.rowsBody = Array.from((this.table?.tableViewChild?.nativeElement as HTMLTableElement)?.tBodies)?.reduce(
        (res, body) => res.concat(...Array.from(body.rows)),
        [] as HTMLTableRowElement[]
      );
      if (this.rowsBody && event.code === Key.ArrowUp) {
        const rowIndex = this.rowsBody.findIndex((row) => row.getAttribute('id_row') === this.visualSelectedRows[0]);
        if (!rowIndex || rowIndex === -1) {
          return;
        }
        this.renderer.removeClass(this.rowsBody[rowIndex], 'visualSelectedRow');
        this.renderer.addClass(this.rowsBody[rowIndex - 1], 'visualSelectedRow');
        this.visualSelectedRows[0] = this.rowsBody[rowIndex - 1].getAttribute('id_row');
        event.preventDefault();

        const rect = this.rowsBody[rowIndex - 1].getBoundingClientRect();
        const topShown = rect.top >= 0;
        if (!topShown) {
          window.scrollTo({ top: window.scrollY + rect.top, behavior: 'smooth' });
        }
      } else if (this.rowsBody && event.code === Key.ArrowDown) {
        const rowIndex = this.rowsBody.findIndex((row) => row.getAttribute('id_row') === this.visualSelectedRows[0]);
        if (rowIndex + 1 === this.rowsBody.length || rowIndex === -1) {
          return;
        }
        this.renderer.removeClass(this.rowsBody[rowIndex], 'visualSelectedRow');
        this.renderer.addClass(this.rowsBody[rowIndex + 1], 'visualSelectedRow');
        this.visualSelectedRows[0] = this.rowsBody[rowIndex + 1].getAttribute('id_row');
        event.preventDefault();

        const rect = this.rowsBody[rowIndex + 1].getBoundingClientRect();
        const bottomShown = rect.bottom <= window.innerHeight - 60;
        if (!bottomShown) {
          window.scrollTo({ top: rect.bottom + window.scrollY - (window.innerHeight - 60), behavior: 'smooth' });
        }
      }
    }
  }

  private handlerSelectedRow() {
    this.rowsBody = Array.from((this.table?.tableViewChild?.nativeElement as HTMLTableElement)?.tBodies)?.reduce(
      (res, body) => res.concat(...Array.from(body.rows)),
      [] as HTMLTableRowElement[]
    );
    if (this.rowsBody?.length) {
      const clearAll = () => {
        if (this.visualSelectedRows?.length) {
          const selectedRows = this.rowsBody.filter((el) =>
            this.visualSelectedRows.includes(el.getAttribute('id_row'))
          );
          for (const row of selectedRows) {
            if (this.visualSelectedRows.includes(row.getAttribute('id_row'))) {
              this.renderer.removeClass(row, 'visualSelectedRow');
            }
          }
          this.visualSelectedRows = [];
        }
      };

      const searchToggleIcon = (element: Element) => {
        if (element.classList?.contains('table-toggler') && element.getAttribute('listener') !== 'true') {
          this.renderer.listen(element, 'click', () => {
            this.handlerSelectedRow();
          });
          element.setAttribute('listener', 'true');
        }

        const children = element.children;
        for (let i = 0; i < children.length; i++) {
          searchToggleIcon(children[i]);
        }
      };

      const toggleRow = (id_row: string, isCtrlKey: boolean) => {
        const elementRow: HTMLTableRowElement = this.rowsBody.find((el) => el.getAttribute('id_row') === id_row);
        if (this.visualSelectedRows.includes(id_row)) {
          const isClickNotCtrlInMultiple = this.visualSelectedRows?.length > 1 && !isCtrlKey;
          if (!isCtrlKey) {
            clearAll();
          }
          if (!isClickNotCtrlInMultiple) {
            this.visualSelectedRows = this.visualSelectedRows.filter((id) => id !== id_row);
            this.renderer.removeClass(elementRow, 'visualSelectedRow');
          } else {
            this.visualSelectedRows.push(id_row);
            this.renderer.addClass(elementRow, 'visualSelectedRow');
          }
        } else {
          if (!isCtrlKey) {
            clearAll();
          }
          this.visualSelectedRows.push(id_row);
          if (elementRow) {
            this.renderer.addClass(elementRow, 'visualSelectedRow');
          }
        }
      };

      for (let i = 0; i !== this.rowsBody.length; i++) {
        searchToggleIcon(this.rowsBody[i]);

        if (!this.rowsBody[i].getAttribute('id_row')) {
          const id_row = `${this.keySelectedRows}_${this.indexVisualSelectedRows++}`;
          const rowElement = this.rowsBody[i];
          let rowPointerDown: PointerDownCoordinates | null = null;
          rowElement.setAttribute('id_row', id_row);
          this.renderer.listen(rowElement, 'mousedown', (event: MouseEvent) => {
            if (this.isInteractiveClickTarget(event)) {
              return;
            }
            rowPointerDown = { x: event.clientX, y: event.clientY };
            // Prevent native text-selection rectangles on Ctrl/Meta/Shift multi-select
            if (event.ctrlKey || event.metaKey || event.shiftKey) {
              event.preventDefault();
            }
          });
          this.renderer.listen(rowElement, 'click', (event: MouseEvent) => {
            if (this.isInteractiveClickTarget(event)) {
              return;
            }
            const isCtrlKey = event.ctrlKey || event.metaKey;
            const pointerDown = rowPointerDown;
            rowPointerDown = null;
            if (isTextSelectionRowClickIntent(rowElement, event, pointerDown)) {
              if (!this.visualSelectedRows.includes(id_row)) {
                toggleRow(id_row, isCtrlKey);
              }
              return;
            }
            toggleRow(id_row, isCtrlKey);
            window.getSelection()?.removeAllRanges();
          });
        }
      }
    }
  }

  private isInteractiveClickTarget(event: MouseEvent): boolean {
    const target = event.target as HTMLElement | null;
    return !!target?.closest(
      'p-checkbox, .p-checkbox, button, a, input, textarea, select, .pi-star, .pi-star-fill, .pi-bars, .table-toggler'
    );
  }

  private setTimer() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => {
      this.handlerSelectedRow();
    }, 1000);
  }
}
